import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { X } from "lucide-react";
import { employeeCodeStructureService } from "../../../services/api";
import DatePicker from "../../Common/DatePicker";
import { DsButton, DsLabel } from "../../../design-system";
import {
  EMPLOYEE_CODE_ORG_LEVEL_OPTIONS,
  getEmployeeCodeTodayDate,
  getEmployeeCodeTomorrowDate,
} from "./employeeCodePatternUtils";

// Employee Code Structure Component
const EmployeeCodeStructure = ({
  darkMode,
  branches,
  departments,
  embedInPatternSettings = false,
}) => {
  const [organizationLevel, setOrganizationLevel] = useState("company");
  const [companyConfig, setCompanyConfig] = useState({
    prefix: "NG",
    digits: 5,
    startingNumber: 1,
    autoReset: "never",
  });
  const [branchConfigs, setBranchConfigs] = useState({});
  const [departmentConfigs, setDepartmentConfigs] = useState({});
  const [effectiveDate, setEffectiveDate] = useState("");
  const [sharedNumberSequence, setSharedNumberSequence] = useState(false);
  const [showNewEntitiesModal, setShowNewEntitiesModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Load saved configurations from API
  useEffect(() => {
    const loadEmployeeCodeStructure = async () => {
      try {
        const data = await employeeCodeStructureService.getCurrentStructure();
        const structure = data.data;

        if (structure) {
          if (structure.patternType === "automated") {
            setCompanyConfig({
              effectiveDate: structure.effectiveDate || "",
              prefix: structure.prefix || "NG",
              digits: structure.numberOfDigits || 5,
              startingNumber: structure.startingNumber || 1,
              autoReset: structure.autoReset || "never",
            });
            setEffectiveDate(structure.effectiveDate || "");
          }
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // No structure found - this is normal for new companies
          console.log("No employee code structure found - using defaults");
        } else {
          console.error("Error loading employee code structure:", error);
        }
      }
    };

    loadEmployeeCodeStructure();
  }, []);

  // Save configuration to API
  const saveConfiguration = async () => {
    try {
      // For now, we'll only support company-level automated configuration
      // Branch and department level configurations can be added later

      // Validate required fields
      if (!effectiveDate) {
        toast.error("Please select an effective date");
        return;
      }

      // Validate company configuration for automated pattern
      if (organizationLevel === "company") {
        if (
          !companyConfig.prefix ||
          !companyConfig.digits ||
          !companyConfig.startingNumber
        ) {
          toast.error(
            "Please fill in all required company configuration fields"
          );
          return;
        }
      } else {
        toast.error(
          "Branch and department level configurations are not yet supported. Please use company level."
        );
        return;
      }

      // Prepare API payload
      const payload = {
        patternType: "automated", // For now, we only support automated for company level
        prefix: companyConfig.prefix,
        numberOfDigits: companyConfig.digits,
        startingNumber: companyConfig.startingNumber,
        autoReset: companyConfig.autoReset,
        effectiveDate: effectiveDate,
      };

      console.log("[Save Config] Sending payload to backend:", payload);

      // Call API to save configuration
      const data = await employeeCodeStructureService.saveStructure(payload);
      console.log("Configuration saved successfully:", data);
      console.log(
        "[Save Config] Response from backend:",
        JSON.stringify(data, null, 2)
      );

      if (data.success === false) {
        toast.error("Failed to save configuration: " + data.message);
        return;
      }

      // Clear form
      setEffectiveDate("");

      // Show success message
      toast.success("Employee code structure saved successfully!");

      // Close any modals if they exist
      setShowNewEntitiesModal(false);
      setShowPreviewModal(false);
    } catch (error) {
      console.error("Error saving configuration:", error);
      console.error("[Save Config] Error details:", {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data,
      });
      // Check if it's a consecutive duplicate error
      if (
        error.response?.status === 400 &&
        error.response?.data?.message?.includes(
          "same configuration consecutively"
        )
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to save configuration");
      }
    }
  };

  // Generate preview codes for current configuration
  const generatePreviewCodes = () => {
    const codes = [];

    if (organizationLevel === "company") {
      for (let i = 1; i <= 5; i++) {
        const number = (companyConfig.startingNumber + i - 1)
          .toString()
          .padStart(companyConfig.digits, "0");
        codes.push(`${companyConfig.prefix}${number}`);
      }
    } else if (organizationLevel === "branch") {
      if (sharedNumberSequence) {
        // For shared number sequence, use global sequence across all branches
        const globalStartingNumber =
          Object.values(branchConfigs)[0]?.startingNumber || 1;
        const globalDigits = Object.values(branchConfigs)[0]?.digits || 5;
        let currentNumber = globalStartingNumber;

        Object.keys(branchConfigs).forEach((branchId) => {
          const branch = branches.find((b) => b.id === parseInt(branchId));
          const config = branchConfigs[branchId];
          if (branch && config) {
            for (let i = 1; i <= 3; i++) {
              const number = currentNumber
                .toString()
                .padStart(globalDigits, "0");
              codes.push(`${config.prefix}${number}`);
              currentNumber++;
            }
          }
        });
      } else {
        // Individual number sequences for each branch
        Object.keys(branchConfigs).forEach((branchId) => {
          const branch = branches.find((b) => b.id === parseInt(branchId));
          const config = branchConfigs[branchId];
          if (branch && config) {
            for (let i = 1; i <= 3; i++) {
              const number = (config.startingNumber + i - 1)
                .toString()
                .padStart(config.digits, "0");
              codes.push(`${config.prefix}${number}`);
            }
          }
        });
      }
    } else if (organizationLevel === "department") {
      if (sharedNumberSequence) {
        // For shared number sequence, use global sequence across all departments
        const globalStartingNumber =
          Object.values(departmentConfigs)[0]?.startingNumber || 1;
        const globalDigits = Object.values(departmentConfigs)[0]?.digits || 5;
        let currentNumber = globalStartingNumber;

        Object.keys(departmentConfigs).forEach((deptId) => {
          const dept = departments.find((d) => d.id === parseInt(deptId));
          const config = departmentConfigs[deptId];
          if (dept && config) {
            for (let i = 1; i <= 3; i++) {
              const number = currentNumber
                .toString()
                .padStart(globalDigits, "0");
              codes.push(`${config.prefix}${number}`);
              currentNumber++;
            }
          }
        });
      } else {
        // Individual number sequences for each department
        Object.keys(departmentConfigs).forEach((deptId) => {
          const dept = departments.find((d) => d.id === parseInt(deptId));
          const config = departmentConfigs[deptId];
          if (dept && config) {
            for (let i = 1; i <= 3; i++) {
              const number = (config.startingNumber + i - 1)
                .toString()
                .padStart(config.digits, "0");
              codes.push(`${config.prefix}${number}`);
            }
          }
        });
      }
    }

    return codes;
  };

  // Handle branch configuration change
  const handleBranchConfigChange = (branchId, field, value) => {
    setBranchConfigs((prev) => ({
      ...prev,
      [branchId]: {
        ...prev[branchId],
        [field]: value,
      },
    }));
  };

  // Handle department configuration change
  const handleDepartmentConfigChange = (deptId, field, value) => {
    setDepartmentConfigs((prev) => ({
      ...prev,
      [deptId]: {
        ...prev[deptId],
        [field]: value,
      },
    }));
  };

  // Handle shared number sequence change
  const handleSharedNumberSequenceChange = (checked) => {
    setSharedNumberSequence(checked);
    if (checked) {
      if (organizationLevel === "branch") {
        // Apply same starting number to all branches
        const currentStartingNumber =
          Object.values(branchConfigs)[0]?.startingNumber || 1;
        const updatedConfigs = {};
        branches.forEach((branch) => {
          updatedConfigs[branch.id] = {
            ...branchConfigs[branch.id],
            startingNumber: currentStartingNumber,
          };
        });
        setBranchConfigs(updatedConfigs);
      } else if (organizationLevel === "department") {
        // Apply same starting number to all departments
        const currentStartingNumber =
          Object.values(departmentConfigs)[0]?.startingNumber || 1;
        const updatedConfigs = {};
        departments.forEach((dept) => {
          updatedConfigs[dept.id] = {
            ...departmentConfigs[dept.id],
            startingNumber: currentStartingNumber,
          };
        });
        setDepartmentConfigs(updatedConfigs);
      }
    }
  };

  // Handle new branches/departments
  const handleNewEntities = () => {
    if (organizationLevel === "branch") {
      const newBranches = branches.filter(
        (branch) => !branchConfigs[branch.id]
      );
      if (newBranches.length > 0) {
        const updatedConfigs = { ...branchConfigs };
        newBranches.forEach((branch) => {
          updatedConfigs[branch.id] = {
            prefix: branch.name.substring(0, 2).toUpperCase(),
            digits: 5,
            startingNumber: sharedNumberSequence
              ? Object.values(branchConfigs)[0]?.startingNumber || 1
              : 1,
          };
        });
        setBranchConfigs(updatedConfigs);
        toast.success(
          `${newBranches.length} new branch(es) added to configuration`
        );
      }
    } else if (organizationLevel === "department") {
      const newDepartments = departments.filter(
        (dept) => !departmentConfigs[dept.id]
      );
      if (newDepartments.length > 0) {
        const updatedConfigs = { ...departmentConfigs };
        newDepartments.forEach((dept) => {
          updatedConfigs[dept.id] = {
            prefix: `NG${dept.name.substring(0, 2).toUpperCase()}`,
            digits: 5,
            startingNumber: sharedNumberSequence
              ? Object.values(departmentConfigs)[0]?.startingNumber || 1
              : 1,
          };
        });
        setDepartmentConfigs(updatedConfigs);
        toast.success(
          `${newDepartments.length} new department(s) added to configuration`
        );
      }
    }
    setShowNewEntitiesModal(false);
  };

  // Prefer shared IST helpers
  const getTomorrowDate = getEmployeeCodeTomorrowDate;
  const getTodayDate = getEmployeeCodeTodayDate;

  const sectionClass = embedInPatternSettings
    ? "space-y-3 border-b border-[hsl(var(--ds-border))] pb-4"
    : "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4";

  return (
    <div className={`space-y-4 pb-2 ${embedInPatternSettings ? "space-y-3 pb-0" : ""}`}>
      {/* Organization Level Selection */}
      <section className={sectionClass}>
        <DsLabel className="mb-2">Organization Level</DsLabel>
        <div
          className="flex flex-wrap gap-x-6 gap-y-2"
          role="radiogroup"
          aria-label="Organization level"
        >
          {EMPLOYEE_CODE_ORG_LEVEL_OPTIONS.map((level) => (
            <label key={level.value} className="flex cursor-pointer items-start gap-2">
              <input
                type="radio"
                name="employeeCodeOrgLevel"
                value={level.value}
                checked={organizationLevel === level.value}
                onChange={() => setOrganizationLevel(level.value)}
                className="mt-1 accent-[hsl(var(--ds-primary))]"
              />
              <span>
                <span className="ds-body block font-medium text-[hsl(var(--ds-fg))]">
                  {level.label}
                </span>
                <span className="ds-caption block text-[hsl(var(--ds-muted-fg))]">
                  {level.description}
                </span>
              </span>
            </label>
          ))}
        </div>
        {organizationLevel !== "company" && (
          <p className="ds-caption mt-2 text-[hsl(var(--ds-warning-fg))]">
            Branch and department levels are preview only. Saving supports company level.
          </p>
        )}
      </section>

      {/* Global Effective Date Section */}
      <section className={sectionClass}>
        <h3 className="ds-h4 mb-2 text-[hsl(var(--ds-fg))]">Effective Date</h3>
        <div className="max-w-sm">
          <DsLabel className="mb-1">
            Effective Date <span className="text-[hsl(var(--ds-danger))]">*</span>
          </DsLabel>
            <DatePicker
              value={effectiveDate || companyConfig.effectiveDate || ""}
              min={getTomorrowDate()}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = getTodayDate();
                if (selectedDate < today) {
                  toast.error(
                    "Please select a future date (tomorrow or later)"
                  );
                  return;
                }
                setEffectiveDate(selectedDate);
                setCompanyConfig((prev) => ({
                  ...prev,
                  effectiveDate: selectedDate,
                }));
              }}
              className="ds-input h-9 w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
            />
            <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
              Future dates only (tomorrow or later).
            </p>
        </div>
      </section>

      {/* Configuration Section */}
      <section
        className={
          embedInPatternSettings
            ? "space-y-3"
            : "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4"
        }
      >
        <h3 className="ds-h4 mb-2 text-[hsl(var(--ds-fg))]">
          {organizationLevel === "company" && "Company Level Configuration"}
          {organizationLevel === "branch" && "Branch Level Configuration"}
          {organizationLevel === "department" && "Department Level Configuration"}
        </h3>

        {organizationLevel === "company" && (
          <div className="space-y-3">
            {/* Configuration Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                >
                  Company Prefix
                </label>
                <input
                  type="text"
                  value={companyConfig.prefix}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      prefix: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g., NG, ABC, XYZ"
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                      : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                >
                  Number of Digits
                </label>
                <select
                  value={companyConfig.digits}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      digits: parseInt(e.target.value),
                    }))
                  }
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                      : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                >
                  <option value={3}>3 digits (001-999)</option>
                  <option value={4}>4 digits (0001-9999)</option>
                  <option value={5}>5 digits (00001-99999)</option>
                  <option value={6}>6 digits (000001-999999)</option>
                  <option value={7}>7 digits (0000001-9999999)</option>
                  <option value={8}>8 digits (00000001-99999999)</option>
                  <option value="custom">Custom (Enter manually)</option>
                </select>
                {companyConfig.digits === "custom" && (
                  <input
                    type="number"
                    min="1"
                    max="10"
                    placeholder="Enter number of digits (1-10)"
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value >= 1 && value <= 10) {
                        setCompanyConfig((prev) => ({
                          ...prev,
                          digits: value,
                        }));
                      }
                    }}
                    className={`w-full p-2 border rounded mt-1 text-sm ${
                      darkMode
                        ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                        : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                    }`}
                  />
                )}
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                >
                  Starting Number
                </label>
                <input
                  type="number"
                  value={companyConfig.startingNumber}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      startingNumber: parseInt(e.target.value),
                    }))
                  }
                  placeholder="e.g., 1"
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                      : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                >
                  Auto Reset
                </label>
                <select
                  value={companyConfig.autoReset}
                  onChange={(e) =>
                    setCompanyConfig((prev) => ({
                      ...prev,
                      autoReset: e.target.value,
                    }))
                  }
                  className={`w-full p-2 border rounded text-sm ${
                    darkMode
                      ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                      : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                  }`}
                >
                  <option value="never">Never</option>
                  <option value="yearly">Yearly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {organizationLevel === "branch" && (
          <div className="space-y-3">
            {/* Shared Number Sequence Option */}
            <div
              className={`p-3 rounded-none border ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4
                    className="font-semibold mb-1 text-[hsl(var(--ds-fg))]"
                  >
                    Shared Number Sequence
                  </h4>
                  <p
                    className="text-xs text-[hsl(var(--ds-muted-fg))]"
                  >
                    Use the same number sequence across all branches (only
                    prefix changes)
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sharedNumberSequence}
                    onChange={(e) =>
                      handleSharedNumberSequenceChange(e.target.checked)
                    }
                    className="sr-only"
                  />
                  <div
                    className={`relative w-8 h-5 transition-colors duration-200 ease-in-out rounded-full ${
                      sharedNumberSequence
                        ? darkMode
                          ? "bg-[hsl(var(--ds-primary))]"
                          : "bg-[hsl(var(--ds-primary))]"
                        : darkMode
                        ? "bg-[hsl(var(--ds-muted))]"
                        : "bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    <div
                      className={`absolute w-3 h-3 bg-[hsl(var(--ds-surface))] rounded-full transition-transform duration-200 ease-in-out transform ${
                        sharedNumberSequence
                          ? "translate-x-4"
                          : "translate-x-0.5"
                      } top-1`}
                    ></div>
                  </div>
                </label>
              </div>
            </div>

            {/* New Branches Notification */}
            {branches.filter((branch) => !branchConfigs[branch.id]).length >
              0 && (
              <div
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-yellow-600 bg-yellow-900"
                    : "border-yellow-300 bg-yellow-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        "text-[hsl(var(--ds-warning-fg))]"
                      }`}
                    >
                      New Branches Detected
                    </h4>
                    <p
                      className={`text-xs ${
                        "text-[hsl(var(--ds-warning-fg))]"
                      }`}
                    >
                      {
                        branches.filter((branch) => !branchConfigs[branch.id])
                          .length
                      }{" "}
                      new branch(es) found. Click "Add New Branches" to include
                      them in the configuration.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewEntitiesModal(true)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode
                        ? "bg-yellow-600 text-[hsl(var(--ds-fg))] hover:bg-yellow-700"
                        : "bg-yellow-500 text-[hsl(var(--ds-fg))] hover:bg-yellow-600"
                    } transition-colors`}
                  >
                    Add New Branches
                  </button>
                </div>
              </div>
            )}

            {branches.length > 0 && (
              <div className="max-h-[min(420px,50vh)] space-y-3 overflow-y-auto overscroll-y-contain rounded-md border p-2 pr-1 dark:border-[hsl(var(--ds-border))]">
            {branches.map((branch) => (
              <div
                key={branch.id}
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
                <h4
                  className="font-semibold mb-2 text-[hsl(var(--ds-fg))]"
                >
                  {branch.name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={branchConfigs[branch.id]?.prefix || ""}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "prefix",
                          e.target.value.toUpperCase()
                        )
                      }
                      placeholder={`e.g., ${branch.name
                        .substring(0, 2)
                        .toUpperCase()}`}
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Number of Digits
                    </label>
                    <select
                      value={branchConfigs[branch.id]?.digits || 5}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "digits",
                          parseInt(e.target.value)
                        )
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <option value={3}>3 digits</option>
                      <option value={4}>4 digits</option>
                      <option value={5}>5 digits</option>
                      <option value={6}>6 digits</option>
                      <option value={7}>7 digits</option>
                      <option value={8}>8 digits</option>
                      <option value="custom">Custom</option>
                    </select>
                    {branchConfigs[branch.id]?.digits === "custom" && (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Enter number of digits (1-10)"
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= 1 && value <= 10) {
                            handleBranchConfigChange(
                              branch.id,
                              "digits",
                              value
                            );
                          }
                        }}
                        className={`w-full p-2 border rounded mt-1 text-sm ${
                          darkMode
                            ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                            : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Starting Number
                      {sharedNumberSequence && (
                        <span
                          className="ml-1 text-xs text-[hsl(var(--ds-secondary))]"
                        >
                          (Shared)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={branchConfigs[branch.id]?.startingNumber || 1}
                      onChange={(e) =>
                        handleBranchConfigChange(
                          branch.id,
                          "startingNumber",
                          parseInt(e.target.value)
                        )
                      }
                      placeholder="e.g., 1"
                      disabled={
                        sharedNumberSequence && branch.id !== branches[0]?.id
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      } ${
                        sharedNumberSequence && branch.id !== branches[0]?.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
              </div>
            )}
          </div>
        )}

        {organizationLevel === "department" && (
          <div className="space-y-3">
            {/* Shared Number Sequence Option */}
            <div
              className={`p-3 rounded-none border ${
                darkMode
                  ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                  : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4
                    className="font-semibold mb-1 text-[hsl(var(--ds-fg))]"
                  >
                    Shared Number Sequence
                  </h4>
                  <p
                    className="text-xs text-[hsl(var(--ds-muted-fg))]"
                  >
                    Use the same number sequence across all departments (only
                    prefix changes)
                  </p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sharedNumberSequence}
                    onChange={(e) =>
                      handleSharedNumberSequenceChange(e.target.checked)
                    }
                    className="sr-only"
                  />
                  <div
                    className={`relative w-8 h-5 transition-colors duration-200 ease-in-out rounded-full ${
                      sharedNumberSequence
                        ? darkMode
                          ? "bg-[hsl(var(--ds-primary))]"
                          : "bg-[hsl(var(--ds-primary))]"
                        : darkMode
                        ? "bg-[hsl(var(--ds-muted))]"
                        : "bg-[hsl(var(--ds-muted))]"
                    }`}
                  >
                    <div
                      className={`absolute w-3 h-3 bg-[hsl(var(--ds-surface))] rounded-full transition-transform duration-200 ease-in-out transform ${
                        sharedNumberSequence
                          ? "translate-x-4"
                          : "translate-x-0.5"
                      } top-1`}
                    ></div>
                  </div>
                </label>
              </div>
            </div>

            {/* New Departments Notification */}
            {departments.filter((dept) => !departmentConfigs[dept.id]).length >
              0 && (
              <div
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-yellow-600 bg-yellow-900"
                    : "border-yellow-300 bg-yellow-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4
                      className={`font-semibold mb-1 ${
                        "text-[hsl(var(--ds-warning-fg))]"
                      }`}
                    >
                      New Departments Detected
                    </h4>
                    <p
                      className={`text-xs ${
                        "text-[hsl(var(--ds-warning-fg))]"
                      }`}
                    >
                      {
                        departments.filter(
                          (dept) => !departmentConfigs[dept.id]
                        ).length
                      }{" "}
                      new department(s) found. Click "Add New Departments" to
                      include them in the configuration.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNewEntitiesModal(true)}
                    className={`px-3 py-1 rounded text-xs font-medium ${
                      darkMode
                        ? "bg-yellow-600 text-[hsl(var(--ds-fg))] hover:bg-yellow-700"
                        : "bg-yellow-500 text-[hsl(var(--ds-fg))] hover:bg-yellow-600"
                    } transition-colors`}
                  >
                    Add New Departments
                  </button>
                </div>
              </div>
            )}

            {departments.length > 0 && (
              <div className="max-h-[min(420px,50vh)] space-y-3 overflow-y-auto overscroll-y-contain rounded-md border p-2 pr-1 dark:border-[hsl(var(--ds-border))]">
            {departments.map((dept) => (
              <div
                key={dept.id}
                className={`p-3 rounded-none border ${
                  darkMode
                    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                }`}
              >
                <h4
                  className="font-semibold mb-2 text-[hsl(var(--ds-fg))]"
                >
                  {dept.name}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Prefix
                    </label>
                    <input
                      type="text"
                      value={departmentConfigs[dept.id]?.prefix || ""}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "prefix",
                          e.target.value.toUpperCase()
                        )
                      }
                      placeholder={`e.g., NG${dept.name
                        .substring(0, 2)
                        .toUpperCase()}`}
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      }`}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Number of Digits
                    </label>
                    <select
                      value={departmentConfigs[dept.id]?.digits || 5}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "digits",
                          parseInt(e.target.value)
                        )
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <option value={3}>3 digits</option>
                      <option value={4}>4 digits</option>
                      <option value={5}>5 digits</option>
                      <option value={6}>6 digits</option>
                      <option value={7}>7 digits</option>
                      <option value={8}>8 digits</option>
                      <option value="custom">Custom</option>
                    </select>
                    {departmentConfigs[dept.id]?.digits === "custom" && (
                      <input
                        type="number"
                        min="1"
                        max="10"
                        placeholder="Enter number of digits (1-10)"
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (value >= 1 && value <= 10) {
                            handleDepartmentConfigChange(
                              dept.id,
                              "digits",
                              value
                            );
                          }
                        }}
                        className={`w-full p-2 border rounded mt-1 text-sm ${
                          darkMode
                            ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                            : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium mb-1 text-[hsl(var(--ds-muted-fg))]"
                    >
                      Starting Number
                      {sharedNumberSequence && (
                        <span
                          className="ml-1 text-xs text-[hsl(var(--ds-secondary))]"
                        >
                          (Shared)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      value={departmentConfigs[dept.id]?.startingNumber || 1}
                      onChange={(e) =>
                        handleDepartmentConfigChange(
                          dept.id,
                          "startingNumber",
                          parseInt(e.target.value)
                        )
                      }
                      placeholder="e.g., 1"
                      disabled={
                        sharedNumberSequence && dept.id !== departments[0]?.id
                      }
                      className={`w-full p-2 border rounded text-sm ${
                        darkMode
                          ? "bg-[hsl(var(--ds-muted))] border-[hsl(var(--ds-border))] text-[hsl(var(--ds-fg))]"
                          : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
                      } ${
                        sharedNumberSequence && dept.id !== departments[0]?.id
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                </div>
              </div>
            ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Action bar — flat strip; sticky only when not embedded under pattern settings */}
      <div
        className={
          embedInPatternSettings
            ? "flex flex-wrap items-center justify-end gap-2 border-t border-[hsl(var(--ds-border))] pt-3"
            : "sticky bottom-0 z-10 border-t border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-3"
        }
      >
        <div className={`flex flex-wrap items-center justify-end gap-2 ${embedInPatternSettings ? "" : "w-full"}`}>
          <DsButton
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowPreviewModal(true)}
          >
            Live Preview
          </DsButton>
          <DsButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setCompanyConfig({
                prefix: "NG",
                digits: 5,
                startingNumber: 1,
                autoReset: "never",
              });
              setBranchConfigs({});
              setDepartmentConfigs({});
              setEffectiveDate("");
              setSharedNumberSequence(false);
              toast.success("Configuration reset successfully!");
            }}
          >
            Reset
          </DsButton>
          <DsButton
            type="button"
            variant="primary"
            size="sm"
            onClick={saveConfiguration}
            disabled={!effectiveDate}
          >
            Save Configuration
          </DsButton>
        </div>
        {!effectiveDate && (
          <p className="ds-caption mt-2 w-full text-right text-[hsl(var(--ds-warning-fg))]">
            Select an effective date above to enable Save.
          </p>
        )}
      </div>

      {/* Live Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            className="p-6 rounded-none shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
          >
            <div className="flex justify-between items-center mb-4">
              <h3
                className="text-lg font-semibold text-[hsl(var(--ds-fg))]"
              >
                Live Preview -{" "}
                {organizationLevel.charAt(0).toUpperCase() +
                  organizationLevel.slice(1)}{" "}
                Level
              </h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]"
              >
                <X size={20} />
              </button>
            </div>
            <div
              className={`p-4 rounded ${
                "bg-[hsl(var(--ds-muted)/0.35)]"
              }`}
            >
              <div className="text-center">
                <p
                  className="text-sm mb-3 text-[hsl(var(--ds-muted-fg))]"
                >
                  Sample generated codes for {organizationLevel} level:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {generatePreviewCodes().map((code, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded ${
                        "bg-[hsl(var(--ds-surface))]"
                      } border ${
                        "border-[hsl(var(--ds-border))]"
                      }`}
                    >
                      <p
                        className={`font-mono text-lg ${
                          "text-[hsl(var(--ds-success))]"
                        }`}
                      >
                        {code}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Entities Modal */}
      {showNewEntitiesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            className="p-6 rounded-none shadow-lg max-w-md w-full mx-4 bg-[hsl(var(--ds-surface))]"
          >
            <h3
              className="text-lg font-semibold mb-4 text-[hsl(var(--ds-fg))]"
            >
              Add New{" "}
              {organizationLevel === "branch" ? "Branches" : "Departments"}
            </h3>
            <p
              className="text-sm mb-4 text-[hsl(var(--ds-muted-fg))]"
            >
              {organizationLevel === "branch"
                ? `${
                    branches.filter((branch) => !branchConfigs[branch.id])
                      .length
                  } new branch(es) will be added with default configurations.`
                : `${
                    departments.filter((dept) => !departmentConfigs[dept.id])
                      .length
                  } new department(s) will be added with default configurations.`}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowNewEntitiesModal(false)}
                className={`px-4 py-2 rounded-none text-sm font-medium ${
                  darkMode
                    ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                    : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] hover:opacity-90"
                } transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleNewEntities}
                className={`px-4 py-2 rounded-none text-sm font-medium ${
                  darkMode
                    ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                    : "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                } transition-colors`}
              >
                Add{" "}
                {organizationLevel === "branch" ? "Branches" : "Departments"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Preview Modal */}
    </div>
  );
};

export default EmployeeCodeStructure;
